<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right"
    @click="open = !open"
  >
    <td class="flex pl-3 py-5 items-center space-x-1">
      <button
        data-test="open-claim"
        type="button"
        class="btn-icon btn-icon-default"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <span>
        {{ title.replace('_', ' ') }}
      </span>
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget) }}
    </td>

    <td class="bg-green-100/50">
      <span v-if="showSubcontractSummary">{{ $tcc(totalCosts.subcontractClaim) }}</span>
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-green-100/50"
    >
      {{ australianCurrency(totalCosts.currentClaim) }}
    </td>

    <td class="bg-green-100/50">
      <span v-if="showSubcontractSummary">{{ $tcc(totalCosts.subcontractCertified) }}</span>
    </td>

    <td class="bg-green-100/50">
      {{ australianCurrency(totalCosts.currentCertified) }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-green-100/50"
      :class="{ 'text-red-600': totalCosts.variance !== 0 }"
    >
      {{ australianCurrency(totalCosts.variance) }}
    </td>

    <td class="bg-green-100/50">
      {{ australianCurrency(totalCosts.previous) }}
    </td>

    <td class="bg-green-100/50 pr-3">
      {{ australianCurrency(totalCosts.cumulative) }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-blue-100/50"
    >
      <span v-if="showPercentages">
        {{ totalCosts.budget > 0 ? australianNumber((totalCosts.currentClaim / totalCosts.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="!claim.capture_claim_amount || showPercentages"
      class="bg-blue-100/50"
    >
      <span v-if="showPercentages">
        {{ totalCosts.budget > 0 ? australianNumber((totalCosts.currentCertified / totalCosts.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.previous / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ totalCosts.budget > 0 ? australianNumber((totalCosts.cumulative / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget - totalCosts.cumulative) }}
    </td>
  </tr>

  <template v-if="open">
    <VariationOrderCostRow
      v-for="variationOrder in uncategorizedOrders"
      :key="variationOrder.id"
      :claim="$props.claim"
      :variation-order="variationOrder"
      :show-percentages="$props.showPercentages"
      :show-subcontract-summary="$props.showSubcontractSummary"
      :variation-order-cost-ids-for-claim-period="props.variationOrderCostIdsForClaimPeriod"
    />

    <VariationOrderCategoryRow
      v-for="group in categorizedGroups"
      :key="group.key"
      :group="group"
      :variation-orders="group.variationOrders"
      :claim="$props.claim"
      :show-percentages="$props.showPercentages"
      :show-subcontract-summary="$props.showSubcontractSummary"
      :variation-order-cost-ids-for-claim-period="props.variationOrderCostIdsForClaimPeriod"
    />

    <tr
      v-if="variationOrders?.length === 0"
      class="bg-gray-50"
    >
      <td
        colspan="100%"
        class="text-center p-4 text-gray-500"
      >
        No {{ title.replace('_', ' ') }} found for this claim
      </td>
    </tr>
  </template>
</template>

<script setup lang="ts">
import { australianCurrency, australianNumber } from '@/helpers';
import Claim from '@/models/Claim';
import VariationOrder, { VariationOrderType } from '@/models/VariationOrder';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { computed, ref } from 'vue';
import VariationOrderCategoryRow from './VariationOrderCategoryRow.vue';
import VariationOrderCostRow from './VariationOrderCostRow.vue';

const props = defineProps<{
  variationOrders?: VariationOrder[];
  claim: Claim;
  title: string;
  variationOrderType: ObjectValues<typeof VariationOrderType>;
  showPercentages?: boolean;
  showSubcontractSummary?: boolean;
  variationOrderCostIdsForClaimPeriod: Id[];
}>();

const categoriesStore = useVariationOrderCategoriesStore();

function sumOrders(orders: VariationOrder[]) {
  const totals = {
    budget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
    variance: 0,
    subcontractClaim: 0,
    subcontractCertified: 0,
  };

  orders.forEach((variationOrder) => {
    totals.budget += variationOrder.totalBudget;
    totals.previous += variationOrder.previous(props.claim);

    const variationOrderCost = variationOrder.costs.find((cost) => {
      return cost.claim_id === props.claim.id;
    });

    if(variationOrderCost) {
      totals.currentClaim += variationOrderCost.current_claim;
      totals.currentCertified += variationOrderCost.current_certified;
      totals.variance += variationOrderCost.current_claim - variationOrderCost.current_certified;
    }

    totals.subcontractClaim += variationOrder.subcontractSummaryClaim(props.variationOrderCostIdsForClaimPeriod);

    totals.subcontractCertified += variationOrder.subcontractSummaryCertified(
      props.variationOrderCostIdsForClaimPeriod,
    );
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
}

const totalCosts = computed(() => sumOrders(props.variationOrders ?? []));

const categorizedGroups = computed(() => {
  const orders = props.variationOrders ?? [];

  const categories = categoriesStore.models
    .where('project_id', props.claim.project_id)
    .whereNull('package_id')
    .orderBy('sort_order')
    .get();

  const groups: {
    key: string;
    label: string;
    variationOrders: VariationOrder[];
  }[] = [];

  categories.forEach((category) => {
    const grouped = orders.filter((vo) => vo.variation_order_category_id === category.id);

    if(!grouped.length) {
      return;
    }

    groups.push({
      key: `category-${category.id}`,
      label: category.description,
      variationOrders: grouped,
    });
  });

  return groups;
});

const uncategorizedOrders = computed(() => {
  return (props.variationOrders ?? []).filter((vo) => !vo.variation_order_category_id);
});

const open = ref(false);

defineOptions({
  inheritAttrs: false,
});
</script>

<style scoped></style>
